'use client';

import React, { useEffect } from 'react';
import { Box, Container, Group, Text, Title } from '@mantine/core';
import Image from 'next/image';
import { useResponsive } from '@/hooks/use-responsive';
import FeatureTabs from '../FeatureTabs';
import { MarkdownContent } from '@/app/page';

interface FeatureTabProps {
    title: string;
    content: React.ReactNode;
    isActive?: boolean;
    onClick?: () => void;
}

const FeatureTab: React.FC<FeatureTabProps> = ({
    title,
    content,
    isActive,
    onClick,
}) => (
    <Box
        onClick={onClick}
        style={{
            cursor: 'pointer',
            padding: '1rem',
            backgroundColor: isActive ? '#f8f9fa' : 'transparent',
            borderRadius: '8px',
            transition: 'all 0.3s ease'
        }}
    >
        <Text fw={500} mb="xs">{title}</Text>
        {isActive && content}
    </Box>
);

interface FeatureSectionProps {
  markdownContent?: MarkdownContent;
}

export default function FeatureSection({ markdownContent }: FeatureSectionProps) {
  // const { content } = useFeatureSectionContent();
  const content = markdownContent?.frontmatter;
  const { responsive } = useResponsive();

  useEffect(() => {
    // console.log('Feature Section Markdown Content:', markdownContent);
  }, [markdownContent]);

  if (!content) return null;

  return (
    <Box
      py={responsive({
        xs: content.layout.padding.xs,
        lg: content.layout.padding.lg
      })}
    >
      <Container size="xl">
        <Group
          style={{
            flexWrap: 'nowrap',
            gap: responsive({
              xs: content.layout.gap.xs,
              lg: content.layout.gap.lg,
              xl: content.layout.gap.xl,
            }),
            flexDirection: responsive({
              xs: content.layout.features.direction.xs,
              lg: content.layout.features.direction.md,
            }),
            maxHeight: responsive({
              lg: content.layout.maxHeight,
            }),
            height: 'auto',
            overflow: 'hidden'
          }}
        >
          <Box
            style={{
              flex: 1,
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <FeatureTabs
              expanded={responsive({
                xs: true,
                md: false,
              })}
              data={content.features.map((feature: { icon: string; }) => ({
                ...feature,
                typography: content.typography.feature
              }))}
            />
          </Box>

          <Box
            style={{
              flex: 1,
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Image
              src={content.image.src}
              width={content.image.width}
              height={content.image.height}
              alt={content.image.alt}
              style={{
                width: '100%',
                maxHeight: content.layout.maxHeight,
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              layout="responsive"
              sizes="(max-width: 768px) 100vw, 50vw"
              quality={75}
            />
          </Box>
        </Group>
      </Container>
    </Box>
  );
}